import { Aes256Gcm, CipherSuite, HkdfSha256 } from "@hpke/core";
import { DhkemX25519HkdfSha256 } from "@hpke/dhkem-x25519";

// Wire formats shared with Android and iOS — see
// docs/specs/chat-test-vectors.json. The `alg`/`v` fields are the
// ciphersuite/version tags that allow future suite migration.

export interface MessageAad {
  conversationId: string;
  senderId: string;
  messageId: string;
  keyVersion: number;
}

export interface EncryptedMessage {
  v: 1;
  alg: "A256GCM";
  iv: string;
  ct: string;
  keyVersion: number;
}

export interface WrapContext {
  conversationId: string;
  granteeUid: string;
  keyVersion: number;
}

export interface WrappedConversationKey {
  v: 1;
  alg: "HPKE-X25519-HKDF-SHA256-A256GCM";
  enc: string;
  ct: string;
  keyVersion: number;
}

/**
 * A freshly generated identity keypair. The private key is the raw 32 X25519
 * bytes; callers zero-fill it when they are done with it.
 */
export interface IdentityKeyPair {
  publicKey: string;
  privateKey: Uint8Array;
}

/** Recovery-key wrap of the private key (PBKDF2-SHA256 → AES-256-GCM). */
export interface WrappedPrivateKey {
  v: 1;
  kdf: "PBKDF2-SHA256";
  iterations: number;
  salt: string;
  iv: string;
  ct: string;
}

/**
 * Passkey (PRF-derived) and trusted-browser (device-key) wrap of the raw
 * 32-byte private key: AES-256-GCM, 12-byte random iv, no AAD.
 */
export interface KeyWrap {
  v: 1;
  alg: "A256GCM";
  iv: string;
  ct: string;
}

const PBKDF2_ITERATIONS = 600_000;

/** HKDF info that binds the PRF output to the chat identity wrap (fixed by the spec). */
export const PRF_WRAP_INFO = "studysync-chat-identity-v1";

const suite = new CipherSuite({
  kem: new DhkemX25519HkdfSha256(),
  kdf: new HkdfSha256(),
  aead: new Aes256Gcm(),
});

export function toB64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

export function fromB64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Web Crypto's DOM types require ArrayBuffer-backed input, while modern TypeScript types a
 * generic Uint8Array as possibly SharedArrayBuffer-backed. Copying at this boundary both satisfies
 * that contract and prevents a caller-owned mutable view from changing during an async operation.
 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

// RFC 9180 §7.2.1 recommends short info values and hpke-js rejects >=128
// bytes; the "|" delimiter is only unambiguous if no component contains it.
const MAX_INFO_BYTES = 128;

function assertContextComponents(...components: string[]): void {
  components.forEach((c) => {
    if (c.includes("|")) {
      throw new Error("Context fields must not contain '|'");
    }
  });
}

function encodeAad(aad: MessageAad): Uint8Array {
  assertContextComponents(aad.conversationId, aad.senderId, aad.messageId);
  return new TextEncoder().encode(
    `${aad.conversationId}|${aad.senderId}|${aad.messageId}|${aad.keyVersion}`,
  );
}

function encodeInfo(ctx: WrapContext): Uint8Array {
  assertContextComponents(ctx.conversationId, ctx.granteeUid);
  const info = new TextEncoder().encode(
    `${ctx.conversationId}|${ctx.granteeUid}|${ctx.keyVersion}`,
  );
  if (info.length >= MAX_INFO_BYTES) {
    throw new Error(`Wrap context exceeds ${MAX_INFO_BYTES} info bytes`);
  }
  return info;
}

export function generateConversationKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/** The 32-byte PRF input chosen once per identity key (`eval.first`). */
export function generatePrfSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

async function importCk(ck: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", toArrayBuffer(ck), "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
}

export async function encryptMessage(
  ck: Uint8Array,
  plaintext: string,
  aad: MessageAad,
): Promise<EncryptedMessage> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv), additionalData: toArrayBuffer(encodeAad(aad)) },
    await importCk(ck),
    new TextEncoder().encode(plaintext),
  );
  return {
    v: 1,
    alg: "A256GCM",
    iv: toB64(iv),
    ct: toB64(new Uint8Array(ct)),
    keyVersion: aad.keyVersion,
  };
}

export async function decryptMessage(
  ck: Uint8Array,
  message: EncryptedMessage,
  aad: MessageAad,
): Promise<string> {
  const pt = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: toArrayBuffer(fromB64(message.iv)),
      additionalData: toArrayBuffer(encodeAad(aad)),
    },
    await importCk(ck),
    toArrayBuffer(fromB64(message.ct)),
  );
  return new TextDecoder().decode(pt);
}

export async function generateIdentityKeyPair(): Promise<IdentityKeyPair> {
  const keyPair = await suite.kem.generateKeyPair();
  return {
    publicKey: toB64(
      new Uint8Array(await suite.kem.serializePublicKey(keyPair.publicKey)),
    ),
    privateKey: new Uint8Array(await suite.kem.serializePrivateKey(keyPair.privateKey)),
  };
}

export async function wrapConversationKey(
  ck: Uint8Array,
  recipientPublicKey: string,
  ctx: WrapContext,
): Promise<WrappedConversationKey> {
  const publicKey = await suite.kem.deserializePublicKey(
    toArrayBuffer(fromB64(recipientPublicKey)),
  );
  const sender = await suite.createSenderContext({
    recipientPublicKey: publicKey,
    info: toArrayBuffer(encodeInfo(ctx)),
  });
  const ct = await sender.seal(toArrayBuffer(ck));
  return {
    v: 1,
    alg: "HPKE-X25519-HKDF-SHA256-A256GCM",
    enc: toB64(new Uint8Array(sender.enc)),
    ct: toB64(new Uint8Array(ct)),
    keyVersion: ctx.keyVersion,
  };
}

/**
 * Unwrap a conversation key with the raw private key bytes. The key object the
 * HPKE library needs is imported per call from those bytes and never retained,
 * so zero-filling the session's `Uint8Array` is enough to forget the key.
 */
export async function unwrapConversationKey(
  wrapped: WrappedConversationKey,
  privateKey: Uint8Array,
  ctx: WrapContext,
): Promise<Uint8Array> {
  const recipientKey = await suite.kem.deserializePrivateKey(toArrayBuffer(privateKey));
  const recipient = await suite.createRecipientContext({
    recipientKey,
    enc: toArrayBuffer(fromB64(wrapped.enc)),
    info: toArrayBuffer(encodeInfo(ctx)),
  });
  const ck = await recipient.open(toArrayBuffer(fromB64(wrapped.ct)));
  return new Uint8Array(ck);
}

// ---------------------------------------------------------------------------
// Recovery-key wrap (PBKDF2-SHA256, 600k iterations, per-wrap salt)
// ---------------------------------------------------------------------------

async function deriveRecoveryWrapKey(
  secret: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt: toArrayBuffer(salt), iterations },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Wrap the raw private key under a recovery key. `secret` is the UTF-8 text the
 * PBKDF2 password is derived from — for the recovery key that is the normalized
 * 32-character string (never the decoded bytes, never the grouped display form).
 */
export async function wrapPrivateKey(
  privateKey: Uint8Array,
  secret: string,
): Promise<WrappedPrivateKey> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveRecoveryWrapKey(secret, salt, PBKDF2_ITERATIONS);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(privateKey),
  );
  return {
    v: 1,
    kdf: "PBKDF2-SHA256",
    iterations: PBKDF2_ITERATIONS,
    salt: toB64(salt),
    iv: toB64(iv),
    ct: toB64(new Uint8Array(ct)),
  };
}

export async function unwrapPrivateKey(
  wrapped: WrappedPrivateKey,
  secret: string,
): Promise<Uint8Array> {
  const key = await deriveRecoveryWrapKey(secret, fromB64(wrapped.salt), wrapped.iterations);
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(fromB64(wrapped.iv)) },
    key,
    toArrayBuffer(fromB64(wrapped.ct)),
  );
  return new Uint8Array(pt);
}

// ---------------------------------------------------------------------------
// Passkey (PRF) and trusted-browser (device key) wraps — same construction
// ---------------------------------------------------------------------------

/**
 * Wrap key = HKDF-SHA256(ikm = the 32-byte PRF output, salt = empty,
 * info = "studysync-chat-identity-v1", L = 32), imported non-extractable.
 */
export async function derivePrfWrapKey(prfOutput: Uint8Array): Promise<CryptoKey> {
  if (prfOutput.byteLength !== 32) {
    throw new Error("PRF output must be 32 bytes");
  }
  const ikm = await crypto.subtle.importKey("raw", toArrayBuffer(prfOutput), "HKDF", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: new ArrayBuffer(0),
      info: toArrayBuffer(new TextEncoder().encode(PRF_WRAP_INFO)),
    },
    ikm,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** A non-extractable AES-256-GCM key for the trusted-browser record (never leaves IndexedDB). */
export async function generateDeviceKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, [
    "encrypt",
    "decrypt",
  ]);
}

export async function wrapPrivateKeyWithKey(
  key: CryptoKey,
  privateKey: Uint8Array,
): Promise<KeyWrap> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    toArrayBuffer(privateKey),
  );
  return { v: 1, alg: "A256GCM", iv: toB64(iv), ct: toB64(new Uint8Array(ct)) };
}

export async function unwrapPrivateKeyWithKey(key: CryptoKey, wrap: KeyWrap): Promise<Uint8Array> {
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(fromB64(wrap.iv)) },
    key,
    toArrayBuffer(fromB64(wrap.ct)),
  );
  return new Uint8Array(pt);
}

export async function wrapPrivateKeyWithPrf(
  prfOutput: Uint8Array,
  privateKey: Uint8Array,
): Promise<KeyWrap> {
  return wrapPrivateKeyWithKey(await derivePrfWrapKey(prfOutput), privateKey);
}

export async function unwrapPrivateKeyWithPrf(
  prfOutput: Uint8Array,
  wrap: KeyWrap,
): Promise<Uint8Array> {
  return unwrapPrivateKeyWithKey(await derivePrfWrapKey(prfOutput), wrap);
}

// ---------------------------------------------------------------------------
// Key fingerprint
// ---------------------------------------------------------------------------

/** SHA-256 over the raw 32-byte public key, as the full 64-hex digest. */
export async function keyFingerprint(publicKey: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", toArrayBuffer(fromB64(publicKey)));
  let hex = "";
  new Uint8Array(digest).forEach((b) => {
    hex += b.toString(16).padStart(2, "0");
  });
  return hex;
}

/** The user-visible form: the first 32 hex characters, upper-case, in 8 groups of 4. */
export function formatFingerprint(digestHex: string): string {
  const groups = digestHex.slice(0, 32).toUpperCase().match(/.{1,4}/g) ?? [];
  return groups.join(" ");
}
